import HomePage from 'src/pages/home';
import Step from 'src/components/Step';
import { useLoaded } from 'src/store/load-context';
import filingInfoSchema from 'src/components/schemas/filingInfoSchema';
import handleNull from 'src/util/handleNull';
import { useAPI } from 'src/services/api';
import { useState, useEffect, useContext } from 'react';
import ToastContext from 'src/store/toast-context';
import ItemizeHelpInfoModal from 'src/components/modal/ItemizeHelpInfoModal';
import StepperContext from 'src/store/stepper-context';
import AuthContext from 'src/store/auth-context';

const HomeStep = (props) => {
  const { setLoaded } = useLoaded();
  const api = useAPI();
  const { updateAccount } = useContext(AuthContext);

  const { nextStep } = useContext(StepperContext);
  const { handleError } = useContext(ToastContext);

  const [initialValues, setInitialValues] = useState({
    paperlessFlag: '',
    bypassItemization: 'N',
    busStatus: '',
    busSameLoc: '',
    soldEquipTo: '',
    dateBusSold: '',
    newBusLicDate: '',
    dateBusClosed: '',
    busLicCancelDate: '',
    equipGoneTo: '',
    bankruptcyType: '',
    bankruptcyCaseno: '',
    bankruptcyDate: '',
    busCity: '',
    busZip: '',
    busLoc: '',
    dateBusMoved: '',
  });

  /**
   * Fetches the initial values from the server, maps the response to the initialValues state,
   * updates the loaded state and calls the handleError in case of error
   * @returns {Promise} a promise that resolves with the initial values data, or rejects with an error.
   */
  const fetchInitialValues = async () => {
    setLoaded(false);
    try {
      const { data } = handleNull(await api.get('/filing/details'));
      setInitialValues(mapResponseToInitialValues(data));
    } catch (err) {
      handleError(err);
    }
    setLoaded(true);
  };

  /**
   * Maps the input data object to an object of initial values for form fields.
   * @param {Object} data - input data object
   * @returns {Object} an object of initial values for form fields.
   */
  const mapResponseToInitialValues = (data) => {
    if (!data) {
      return {};
    }

    return {
      paperlessFlag: data.paperless,
      bypassItemization: 'N',
      busStatus: extractValue(data, ['business', 'status']),
      busSameLoc: extractValue(data, ['business', 'soldBusiness', 'busSameLoc']),
      soldEquipTo: extractValue(data, ['business', 'soldBusiness', 'soldEquipTo']),
      dateBusSold: extractValue(data, ['business', 'soldBusiness', 'dateBusSold']),
      newBusLicDate: extractValue(data, ['business', 'soldBusiness', 'newBusLicDate']),
      dateBusClosed: extractValue(data, ['business', 'closedBusiness', 'dateBusClosed']),
      busLicCancelDate: extractValue(data, ['business', 'closedBusiness', 'busLicCancelDate']),
      equipGoneTo: extractValue(data, ['business', 'closedBusiness', 'equipGoneTo']),
      bankruptcyType: extractValue(data, ['business', 'filedForBankruptcy', 'bankruptcyType']),
      bankruptcyCaseno: extractValue(data, ['business', 'filedForBankruptcy', 'bankruptcyCaseno']),
      bankruptcyDate: extractValue(data, ['business', 'filedForBankruptcy', 'bankruptcyDate']),
      bankruptcyState: extractValue(data, ['business', 'filedForBankruptcy', 'bankruptcyState']),
      busInOper: extractValue(data, ['business', 'filedForBankruptcy', 'busInOper']),

      busCity: extractValue(data, ['business', 'movedBusiness', 'busCity']),
      busZip: extractValue(data, ['business', 'movedBusiness', 'busZip']),
      busLoc: extractValue(data, ['business', 'movedBusiness', 'busLoc']),
      dateBusMoved: extractValue(data, ['business', 'movedBusiness', 'dateBusMoved']),
    };
  };

  /**
   * Extract the value at a given path in an object, or a default value if not found
   * @param {Object} obj - The object to extract the value from
   * @param {string[]} keys - An array of strings representing the path to the desired value in the object
   * @param {any} [defaultValue=''] - The default value to return if the path is not found in the object
   * @returns {any} The value at the given path in the object, or the default value if not found
   */
  const extractValue = (obj, keys, defaultValue = '') => {
    return keys.reduce((result, key) => (result && result[key]) || defaultValue, obj);
  };

  /**
   * Submits form data to the server via a PATCH request,
   * then updates the initialValues state, and calls nextStep function.
   * @param {Object} values - an object containing the form field values.
   * @returns {Promise} a promise that resolves when the form data has been successfully submitted, or rejects with an error.
   */

  const submitFormData = async (values) => {
    try {
      await api.patch(`/business`, { ...values, paperlessFlag: Number(values.paperlessFlag) });
      setInitialValues(values);

      nextStep();
      updateAccount({ bypassItemization: values.bypassItemization });
    } catch (e) {
      console.error(e);
      handleError(e);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => fetchInitialValues(), []);

  return (
    <Step
      modals={[<ItemizeHelpInfoModal key="itemizeHelpInfoModal" />]}
      component={<HomePage />}
      onComplete={submitFormData}
      initialValues={initialValues}
      schema={filingInfoSchema}
      path={['/home']}
      defaultPath={'/home'}
      {...props}
    />
  );
};

export default HomeStep;
